// ─── Version History Page ─────────────────────────────────────────────────────
// Shows thesis export snapshots and chapter version history.
// Allows restoring chapters to previous versions and loading snapshots for export.

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, Clock, RotateCcw, Download, Shield, ChevronDown, ChevronRight, Loader2, AlertTriangle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { listSnapshots, loadSnapshot, listChapterVersions, restoreChapterVersion, createSnapshot } from '../../utils/snapshotEngine'
import { logEvent, EVENT } from '../../utils/logger'
import toast from 'react-hot-toast'

const STATUS_COLOR = {
  READY: { bg: '#d1fae5', text: '#065f46' },
  DRAFT: { bg: '#e0f2fe', text: '#0c4a6e' },
}

function SnapshotCard({ snap, onExportFrom }) {
  const s = STATUS_COLOR[snap.export_status] || STATUS_COLOR.DRAFT
  const date = new Date(snap.created_at).toLocaleString()

  return (
    <div style={{ border: '1px solid #DDE4EF', borderRadius: 10, padding: '1rem', marginBottom: 8, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield style={{ width: 14, height: 14, color: '#2563eb' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Version {snap.version_number}</span>
          {snap.label && <span style={{ fontSize: 11, color: '#64748b' }}>— {snap.label}</span>}
        </div>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: s.bg, color: s.text }}>
          {snap.export_status}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b', marginBottom: 8 }}>
        <span>Score: <strong style={{ color: snap.export_score >= 80 ? '#059669' : snap.export_score >= 50 ? '#d97706' : '#dc2626' }}>{snap.export_score}/100</strong></span>
        <span>Created: {date}</span>
        {snap.created_by && <span>By: {snap.created_by}</span>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => onExportFrom(snap)}
          style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
        >
          <Download style={{ width: 11, height: 11, display: 'inline', marginRight: 3 }} />
          Export this version
        </button>
      </div>
    </div>
  )
}

function ChapterVersionPanel({ chapterId, chapterTitle, thesisId }) {
  const [open,     setOpen]     = useState(false)
  const [versions, setVersions] = useState([])
  const [loading,  setLoading]  = useState(false)
  const [restoring,setRestoring]= useState(null)

  const load = async () => {
    setLoading(true)
    try {
      const v = await listChapterVersions(chapterId)
      setVersions(v)
    } finally {
      setLoading(false)
    }
  }

  const handleOpen = () => {
    setOpen(o => !o)
    if (!open && versions.length === 0) load()
  }

  const handleRestore = async (version) => {
    if (!window.confirm(`Restore Chapter to version ${version.version_num} (${version.event_type}, ${new Date(version.created_at).toLocaleDateString()})? This will overwrite the current content.`)) return
    setRestoring(version.id)
    try {
      await restoreChapterVersion(chapterId, version.id)
      await logEvent(EVENT.ROLLBACK_CHAPTER, { thesisId, metadata: { chapterId, versionNum: version.version_num } })
      toast.success(`Chapter restored to version ${version.version_num}`)
    } catch (err) {
      toast.error('Restore failed: ' + err.message)
    } finally {
      setRestoring(null)
    }
  }

  const EVENT_COLORS = { REGENERATE: '#7c3aed', SAVE: '#2563eb', FINALIZE: '#059669', ROLLBACK: '#dc2626' }

  return (
    <div style={{ border: '1px solid #DDE4EF', borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
      <button
        onClick={handleOpen}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', background: '#f8fafc', border: 'none', cursor: 'pointer' }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{chapterTitle}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {versions.length > 0 && <span style={{ fontSize: 10, color: '#94a3b8' }}>{versions.length} version(s)</span>}
          {open ? <ChevronDown style={{ width: 12, height: 12, color: '#64748b' }} /> : <ChevronRight style={{ width: 12, height: 12, color: '#64748b' }} />}
        </div>
      </button>

      {open && (
        <div style={{ padding: '0.5rem 0.75rem' }}>
          {loading && <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Loading versions…</p>}
          {!loading && versions.length === 0 && (
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>No version history yet. Versions are saved on each generate/finalize.</p>
          )}
          {versions.map(v => (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: `${EVENT_COLORS[v.event_type] || '#64748b'}20`, color: EVENT_COLORS[v.event_type] || '#64748b' }}>
                  {v.event_type}
                </span>
                <span style={{ fontSize: 11, color: '#334155' }}>v{v.version_num}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>{v.word_count?.toLocaleString()} words</span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{new Date(v.created_at).toLocaleString()}</span>
              </div>
              <button
                onClick={() => handleRestore(v)}
                disabled={restoring === v.id}
                style={{ fontSize: 10, fontWeight: 600, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}
              >
                {restoring === v.id ? '…' : <><RotateCcw style={{ width: 9, height: 9, display: 'inline', marginRight: 2 }} />Restore</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function VersionHistoryPage() {
  const { id: thesisId } = useParams()
  const navigate = useNavigate()

  const [thesis,    setThesis]    = useState(null)
  const [chapters,  setChapters]  = useState([])
  const [snapshots, setSnapshots] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [creating,  setCreating]  = useState(false)
  const [label,     setLabel]     = useState('')
  const [userEmail, setUserEmail] = useState('')

  useEffect(() => {
    if (!thesisId) return
    const load = async () => {
      const [
        { data: t },
        { data: chs },
        snaps,
        { data: { user } },
      ] = await Promise.all([
        supabase.from('theses').select('id, title').eq('id', thesisId).maybeSingle(),
        supabase.from('chapters').select('id, chapter_number, title, status, word_count').eq('thesis_id', thesisId).order('chapter_number'),
        listSnapshots(thesisId).catch(() => []),
        supabase.auth.getUser(),
      ])
      setThesis(t)
      setChapters(chs || [])
      setSnapshots(snaps)
      setUserEmail(user?.email || '')
      setLoading(false)
    }
    load()
  }, [thesisId])

  const handleCreateSnapshot = async () => {
    setCreating(true)
    try {
      const snap = await createSnapshot(thesisId, { label, userEmail })
      setSnapshots(prev => [snap, ...prev])
      setLabel('')
      toast.success(`Snapshot v${snap.version_number} created — score: ${snap.export_score}/100`)
    } catch (err) {
      toast.error('Snapshot failed: ' + err.message)
    } finally {
      setCreating(false)
    }
  }

  const handleExportFrom = (snap) => {
    navigate(`/app/thesis/${thesisId}/export?snapshot=${snap.id}`)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Loader2 style={{ width: 24, height: 24, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '2rem 1rem' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to={`/app/thesis/${thesisId}/export`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b', textDecoration: 'none', marginBottom: 8 }}>
          <ChevronLeft style={{ width: 12, height: 12 }} /> Export
        </Link>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>Version History</h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>{thesis?.title}</p>
      </div>

      {/* Create snapshot panel */}
      <div style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          <Shield style={{ width: 14, height: 14, display: 'inline', marginRight: 6, color: '#2563eb' }} />
          Create Export Snapshot
        </h2>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: '0.75rem', lineHeight: 1.5 }}>
          Freezes the current thesis state. Exports use snapshots — not live data — so figures, numbering, and content are permanently locked.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Version label (optional, e.g. 'Final submission')"
            style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #DDE4EF', borderRadius: 8, fontSize: 13, outline: 'none' }}
          />
          <button
            onClick={handleCreateSnapshot}
            disabled={creating}
            style={{ padding: '0.5rem 1rem', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: creating ? 0.7 : 1 }}
          >
            {creating ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Shield style={{ width: 14, height: 14 }} />}
            {creating ? 'Creating…' : 'Create Snapshot'}
          </button>
        </div>
      </div>

      {/* Snapshots */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: '0.75rem' }}>
          Export Snapshots ({snapshots.length})
        </h2>
        {snapshots.length === 0 && (
          <div style={{ padding: '1.5rem', border: '1px dashed #DDE4EF', borderRadius: 10, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No snapshots yet. Create one above before exporting.
          </div>
        )}
        {snapshots.map(snap => (
          <SnapshotCard key={snap.id} snap={snap} onExportFrom={handleExportFrom} />
        ))}
      </div>

      {/* Chapter versions */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: '0.75rem' }}>
          Chapter Version History
        </h2>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: '0.75rem' }}>
          Every generate and finalize event creates a version. Restore any chapter to a previous state.
        </p>
        {chapters.map(ch => (
          <ChapterVersionPanel
            key={ch.id}
            chapterId={ch.id}
            chapterTitle={`Chapter ${ch.chapter_number}: ${ch.title}`}
            thesisId={thesisId}
          />
        ))}
      </div>
    </div>
  )
}
